/**
 * Toggle-fullscreen button. Uses the Fullscreen API on desktop + iPad.
 * iPhone Safari does not support programmatic fullscreen, so we show a toast
 * telling the user how to get fullscreen via Safari's chrome or a home-screen
 * install.
 */

interface FullscreenDoc extends Document {
  webkitFullscreenElement?: Element;
  webkitExitFullscreen?: () => Promise<void>;
}
interface FullscreenEl extends HTMLElement {
  webkitRequestFullscreen?: () => Promise<void>;
}

export function buildFullscreenButton(): void {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.title = 'Toggle fullscreen';
  btn.textContent = '⛶';
  // Right offset sits just left of the minimap — 300 px (desktop) vs 160 px
  // (iPhone, since the minimap shrinks).
  const isMobileUa = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const rightOffset = isMobileUa ? 160 : 300;
  btn.style.cssText = [
    'position:fixed',
    'top:8px', `right:${rightOffset}px`,
    'width:32px', 'height:32px',
    'background:rgba(15,20,25,0.78)',
    'color:#eee',
    'border:1px solid rgba(255,255,255,0.15)',
    'border-radius:6px',
    'font:16px/1 system-ui,sans-serif',
    'cursor:pointer',
    'z-index:6',
    'padding:0',
    'user-select:none',
    '-webkit-user-select:none',
    'touch-action:manipulation',
  ].join(';');

  const refresh = () => {
    const d = document as FullscreenDoc;
    const on = !!(document.fullscreenElement || d.webkitFullscreenElement);
    btn.style.background = on ? 'rgba(80,120,180,0.6)' : 'rgba(15,20,25,0.78)';
  };

  const isIPhone = /iPhone|iPod/i.test(navigator.userAgent);

  function showToast(text: string): void {
    const t = document.createElement('div');
    t.textContent = text;
    t.style.cssText = [
      'position:fixed', 'top:60px', 'left:50%', 'transform:translateX(-50%)',
      'max-width:80vw',
      'background:rgba(15,20,25,0.95)',
      'color:#eee',
      'border:1px solid rgba(255,255,255,0.15)',
      'border-radius:8px',
      'padding:10px 14px',
      'font:12px/1.4 system-ui,sans-serif',
      'text-align:center',
      'z-index:100',
      'box-shadow:0 4px 16px rgba(0,0,0,0.6)',
    ].join(';');
    document.body.appendChild(t);
    setTimeout(() => t.remove(), 5500);
  }

  btn.addEventListener('click', async () => {
    const d = document as FullscreenDoc;
    const root = document.documentElement as FullscreenEl;
    const el = document.fullscreenElement || d.webkitFullscreenElement;

    // Already in fullscreen → exit.
    if (el) {
      const exit = document.exitFullscreen
        ? document.exitFullscreen.bind(document)
        : d.webkitExitFullscreen?.bind(document);
      await exit?.();
      refresh();
      return;
    }

    const req = root.requestFullscreen
      ? root.requestFullscreen.bind(root)
      : root.webkitRequestFullscreen?.bind(root);

    if (req) {
      try {
        await req();
        refresh();
        return;
      } catch (err) {
        console.warn('Fullscreen failed:', err);
      }
    }

    // Fallback for iPhone / unsupported browsers: explain how to get fullscreen.
    if (isIPhone) {
      showToast('iPhone Safari blocks programmatic fullscreen. Tap the "aA" icon in the URL bar → "Hide Toolbar", or "Share → Add to Home Screen" and launch from the icon.');
    } else {
      showToast('Fullscreen API unavailable on this browser.');
    }
  });

  document.addEventListener('fullscreenchange', refresh);
  document.addEventListener('webkitfullscreenchange', refresh);

  document.body.appendChild(btn);
}
